import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Tag, ChevronRight, X, ExternalLink, ChevronDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PromotionsSection = ({ promotions, content }) => {
  const sectionPromos = promotions.filter(p => p.show_section && p.is_active);
  const cfg = content?.promotions || { badge: "Offres Spéciales", title: "Nos Promotions" };

  if (sectionPromos.length === 0) return null;

  return (
    <section id="promotions" className="py-16 px-4 bg-gradient-to-b from-[#0A0A0A] to-[#121212]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-4">
            {cfg.badge}
          </p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase text-white">
            {cfg.title}
          </h2>
        </div>
        
        <div className={`grid gap-6 ${
          sectionPromos.length === 1 
            ? 'grid-cols-1 max-w-md mx-auto' 
            : sectionPromos.length === 2 
              ? 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto' 
              : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
        }`}>
          {sectionPromos.map((promo) => (
            <div
              key={promo.id}
              data-testid={`promo-card-${promo.id}`}
              className="bg-[#1C1C1E] border border-[#FF3B30]/30 rounded-lg overflow-hidden hover:border-[#FF3B30] transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,59,48,0.2)]"
            >
              <div className="bg-gradient-to-r from-[#FF3B30] to-[#FF6B30] p-4">
                <div className="flex items-center justify-between">
                  <Tag size={24} className="text-white" />
                  {promo.discount && (
                    <span className="bg-white text-[#FF3B30] font-black text-lg px-3 py-1 rounded">
                      {promo.discount}
                    </span>
                  )}
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-2">{promo.title}</h3>
                <p className="text-white/70 text-sm mb-4">{promo.description}</p>
                {promo.valid_until && (
                  <p className="text-[#FF3B30] text-xs">
                    Valable jusqu&apos;au {new Date(promo.valid_until).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============== GALLERY SECTION ==============
export const GallerySection = ({ images }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  
  if (!images || images.length === 0) return null;
  
  return (
    <section id="galerie" className="py-20 px-4 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-4">
            Nos Réalisations
          </p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase text-white">
            Galerie Photos
          </h2>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((img) => (
            <div
              key={img.id}
              data-testid={`gallery-image-${img.id}`}
              onClick={() => setSelectedImage(img)}
              className="relative aspect-square overflow-hidden rounded-lg cursor-pointer group"
            >
              <img
                src={img.url}
                alt={img.caption || "Réalisation Auvergne Tech Racing"}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {img.caption && (
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white text-sm">{img.caption}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X size={32} />
          </button>
          <img
            src={selectedImage.url}
            alt={selectedImage.caption || "Réalisation"}
            className="max-w-full max-h-[90vh] object-contain"
          />
          {selectedImage.caption && (
            <p className="absolute bottom-8 left-0 right-0 text-center text-white text-lg">
              {selectedImage.caption}
            </p>
          )}
        </div>
      )}
    </section>
  );
};

// ============== HERO SECTION ==============
export const VehicleSelectorWidget = () => {
  useEffect(() => {
    // Load the Bensky iframeResizer script
    const script = document.createElement('script');
    script.src = 'https://portal.bensky.co.uk/assets/js/iframeResizer.min.js';
    script.async = true;
    script.onload = () => {
      // Initialize iframe resizer after script loads
      if (window.iFrameResize) {
        window.iFrameResize(false, "#v_stats_widget1639860378");
      }
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup on unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <section id="vehicle-selector" className="py-16 md:py-24 px-4 bg-[#0A0A0A]">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF3B30] mb-4">
            Mise à jour calculateur
          </p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase text-white mb-4">
            Trouvez Votre Véhicule
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            Sélectionnez votre véhicule pour découvrir les gains de puissance et de couple disponibles avec notre service de mise à jour calculateur.
          </p>
        </div>

        {/* Widget Container with iframe */}
        <div 
          data-testid="vehicle-selector-widget"
          className="bg-[#121212] border border-white/10 rounded-lg p-6 md:p-8 overflow-hidden"
        >
          <iframe 
            src="https://portal.bensky.co.uk/w.php?id=1739&theme=light&color=ff0000&differencecolor=474747" 
            style={{ border: '0px', transition: 'height 0.5s' }}
            width="100%" 
            height="200" 
            allow="autoplay"
            id="v_stats_widget1639860378" 
            referrerPolicy="origin" 
            title="Vehicle search"
          />
        </div>
        <div className="mt-6 text-center">
          <a
            href="/shop"
            data-testid="vehicle-selector-shop-link"
            className="inline-flex items-center gap-2 text-[#FF3B30] hover:text-[#ff6b62] text-sm font-semibold transition-colors"
          >
            Besoin de pièces ? Visitez notre boutique
            <ChevronRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
};

// ============== FLEXFUEL CALCULATOR ==============
